// @ts-nocheck — interacts with untyped store
import {useMemo} from "react";
import {useNavigate} from "react-router-dom";
import fallbackImage from "@/assets/images/fallback-image.svg";
import {GameCarouselRow} from "@/components";
import {useGetTopGames} from "@/hooks/gamesList/useGetTopGames.tsx";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import {
  gameCardCls,
  gameCardImageCls,
  gameCardNameCls,
  gameCardOverlayCls,
  gameCardPlayBtnCls,
} from "../_gameCardShared";

// ─── keyword lists ────────────────────────────────────────────────────────────
const OLYMPUS_KEYWORDS = [
  "olympus",
  "zeus",
  "hades",
  "poseidon",
  "athena",
  "titan",
  "kronos",
  "medusa",
  "pegasus",
  "apollo",
  "hermes",
  "dionysus",
  "ares",
  "aphrodite",
  "hera",
  "olymp",
  "olympian",
];

const matchesAny = (name: string, keywords: string[]) => {
  const lower = (name ?? "").toLowerCase();
  return keywords.some(k => lower.includes(k));
};

// ─── inline card ─────────────────────────────────────────────────────────────
const SlotCard = ({game, onPlay}) => (
  <div
    className={gameCardCls}
    role="button"
    tabIndex={0}
    aria-label={`Jugar ${game.name}`}
    onClick={() => onPlay(game.provider, game.id)}
    onKeyDown={e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onPlay(game.provider, game.id);
      }
    }}
  >
    <img
      src={game.icon || fallbackImage}
      alt={game.name ?? ""}
      className={gameCardImageCls}
      onError={e => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = fallbackImage;
      }}
    />
    <div className={gameCardOverlayCls}>
      <span className={gameCardNameCls}>{game.name}</span>
      <button
        type="button"
        className={gameCardPlayBtnCls}
        onClick={e => {
          e.stopPropagation();
          onPlay(game.provider, game.id);
        }}
        tabIndex={-1}
      >
        Jugar Ya
      </button>
    </div>
  </div>
);

// ─── main component ───────────────────────────────────────────────────────────
const CatalogCarousels = () => {
  const allGames = useMiniGames(state => state.list) ?? [];
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const {data: topData} = useGetTopGames({});

  const handlePlay = (provider, gameId) => {
    if (authStatus === "authenticated") {
      navigate(`/games/${encodeURIComponent(provider)}/${encodeURIComponent(gameId)}`);
    } else {
      handleShowAuthModal();
    }
  };

  // ── provider carousels ──
  const pragmaticGames = useMemo(
    () => allGames.filter(g => g.provider?.toLowerCase().includes("pragmatic")).slice(0, 24),
    [allGames]
  );
  const bgamingGames = useMemo(
    () => allGames.filter(g => g.provider?.toLowerCase().includes("bgaming")).slice(0, 24),
    [allGames]
  );
  const evoplayGames = useMemo(
    () => allGames.filter(g => g.provider?.toLowerCase().includes("evoplay")).slice(0, 24),
    [allGames]
  );

  // ── popular (first N games — API returns in popularity order) ──
  const popularGames = useMemo(() => allGames.slice(0, 30), [allGames]);

  // ── top 10 enriched from API ──
  const top10Games = useMemo(() => {
    const raw = topData?.data?.data ?? [];
    return raw
      .slice(0, 10)
      .map(tg =>
        allGames.find(g => g.id === tg.game_id && g.provider?.toLowerCase() === tg.game_provider?.toLowerCase())
      )
      .filter(Boolean);
  }, [topData, allGames]);

  // ── thematic carousels ──
  const caiShenGames = useMemo(
    () =>
      allGames.filter(g => {
        const n = (g.name ?? "").toLowerCase().replace(/[\s_-]/g, "");
        return n.includes("caishen") || (g.name ?? "").toLowerCase().includes("cai shen");
      }),
    [allGames]
  );

  const olympusGames = useMemo(() => allGames.filter(g => matchesAny(g.name, OLYMPUS_KEYWORDS)), [allGames]);

  const fruitGames = useMemo(() => allGames.filter(g => (g.name ?? "").toLowerCase().includes("fruit")), [allGames]);

  const cards = games =>
    games.map(g => (
      <SlotCard
        key={`${g.provider}-${g.id}`}
        game={g}
        onPlay={handlePlay}
      />
    ));

  return (
    <div className="w-full flex flex-col gap-10 px-4 min-[768px]:px-8 min-[1200px]:px-[130px] py-6">
      {/* ── Pragmatic ── */}
      {pragmaticGames.length > 0 && (
        <GameCarouselRow
          title="Pragmatic Play"
          subtitle="Los juegos más jugados del planeta"
          viewAllPath="/games/provider/pragmatic"
        >
          {cards(pragmaticGames)}
        </GameCarouselRow>
      )}

      {/* ── BGaming ── */}
      {bgamingGames.length > 0 && (
        <GameCarouselRow
          title="BGaming"
          subtitle="Innovadores del slot moderno"
          viewAllPath="/games/provider/bgaming"
        >
          {cards(bgamingGames)}
        </GameCarouselRow>
      )}

      {/* ── Evoplay ── */}
      {evoplayGames.length > 0 && (
        <GameCarouselRow
          title="Evoplay"
          subtitle="Experiencias visuales únicas"
          viewAllPath="/games/provider/evoplay"
        >
          {cards(evoplayGames)}
        </GameCarouselRow>
      )}

      {/* ── Los reyes del slot ── */}
      {popularGames.length > 0 && (
        <GameCarouselRow
          icon="👑"
          title="Los reyes del slot"
          subtitle="Los más jugados de toda la corona"
          viewAllPath="/gameslist/all"
        >
          {cards(popularGames)}
        </GameCarouselRow>
      )}

      {/* ── El top 10 ── */}
      {top10Games.length > 0 && (
        <GameCarouselRow
          icon="🏆"
          title="El top 10"
          subtitle="Los 10 que más plata sueltan"
          viewAllPath="/gameslist/all"
        >
          {cards(top10Games)}
        </GameCarouselRow>
      )}

      {/* ── Coronando con el Chino ── */}
      {caiShenGames.length > 0 && (
        <GameCarouselRow
          icon="🀄"
          title="Coronando con el Chino"
          subtitle="La fortuna viene del oriente"
          viewAllPath="/gameslist/all"
        >
          {cards(caiShenGames)}
        </GameCarouselRow>
      )}

      {/* ── Coronando en el Olympus ── */}
      {olympusGames.length > 0 && (
        <GameCarouselRow
          icon="⚡"
          title="Coronando en el Olympus"
          subtitle="Los dioses del slot están aquí"
          viewAllPath="/gameslist/all"
        >
          {cards(olympusGames)}
        </GameCarouselRow>
      )}

      {/* ── Las frutas de la corona ── */}
      {fruitGames.length > 0 && (
        <GameCarouselRow
          icon="🍒"
          title="Las frutas de la corona"
          subtitle="Dulces y que pagan rico"
          viewAllPath="/gameslist/all"
        >
          {cards(fruitGames)}
        </GameCarouselRow>
      )}
    </div>
  );
};

export default CatalogCarousels;
